import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getRequest, postRequest } from "@/utils";
import { CloseIcon, LoadingIcon } from "@/components/ui/icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Control, Controller, useForm, UseFormSetValue } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { EmailField, SingleEmailField } from "./email-field";
import { CCBCCOptions } from "./cc-bcc-options";
import { useNewEmailStore } from "../store/new-email-store";

const newEmailSchema = z.object({
  to: z
    .array(
      z.object({ label: z.string(), value: z.email(), leadName: z.string() }),
    )
    .min(1, "At least one recipient is required"),
  cc: z
    .array(
      z.object({ label: z.string(), value: z.email(), leadName: z.string() }),
    )
    .optional(),
  bcc: z
    .array(
      z.object({ label: z.string(), value: z.email(), leadName: z.string() }),
    )
    .optional(),
  from: z
    .array(
      z.object({ label: z.string(), value: z.email() }),
    )
    .min(1, "From email is required"),
  subject: z.string().optional(),
  body: z.string().min(1, "Body is required"),
});

type newEmailSchema = z.infer<typeof newEmailSchema>;
export type SetValueType = UseFormSetValue<newEmailSchema>;
export type ControlType = Control<newEmailSchema>;
export type FieldKeys = keyof newEmailSchema;

const NewEmailDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { sendingTo, replyTo, leadId } = useNewEmailStore();
  const [cc, setCc] = useState<boolean>(false);
  const [bcc, setBcc] = useState<boolean>(false);
  const { mutate: sendEmail, isPending } = useMutation({
    mutationFn: async (data: newEmailSchema) => {
      const response = await postRequest(
        `mail/send-message`,
        data,
        false,
        undefined,
        "default",
        undefined,
      );
      return response.data;
    },
  });
  const { control, handleSubmit, setValue, reset } = useForm<newEmailSchema>({
    resolver: zodResolver(newEmailSchema),
    defaultValues: {
      to: [],
      cc: [],
      bcc: [],
      from: [],
      subject: "",
      body: "",
    },
  });
  const [query, setQuery] = useState<string | null>(null);
  const { data: myEmails } = useQuery({
    queryKey: [`get-my-emails`],
    queryFn: async () => {
      const response = await getRequest(`mail/my-emails`, "default");

      return response.data?.map((e: { email: string }) => ({
        label: e.email,
        value: e.email,
      })) || [];
    },
    enabled: open,
  });
  const { data: leads } = useQuery({
    queryKey: [`get-lead-mails:${query}:${leadId}`],
    queryFn: async () => {
      // If leadId is set, fetch only contacts from that specific lead
      if (leadId) {
        const response = await postRequest(
          "leads/contact/get",
          { lead_id: leadId },
          false,
          undefined,
          "default",
          undefined
        );
        
        // Transform contacts to MultipleSelector format
        const contacts = response?.data?.contacts || [];
        return contacts
          .filter((c: any) => c.email) // Only contacts with email
          .filter((c: any) => {
            // Apply search filter if query exists
            if (!query) return true;
            const searchLower = query.toLowerCase();
            return (
              c.email.toLowerCase().includes(searchLower) ||
              (c.name && c.name.toLowerCase().includes(searchLower))
            );
          })
          .map((c: any) => ({
            value: c.email,
            label: c.name ? `${c.name} <${c.email}>` : c.email,
            leadName: "", // Not needed when filtering by specific lead
          }));
      } else {
        // Use existing endpoint for all leads
        const response = await getRequest(
          `mail/get-lead-mails?query=${query}`,
          "default"
        );
        return response.data;
      }
    },
    enabled: open,
  });

  useEffect(() => {
    if (sendingTo) {

      const contactInfo = leads?.find((lead: any) => lead.value === sendingTo);
      setValue("to", [{ 
        label: contactInfo?.label || sendingTo, 
        value: sendingTo, 
        leadName: contactInfo?.leadName || "" 
      }]);
    } else if (leadId && leads && leads.length > 0) {

      setValue("to", [{ 
        label: leads[0].label,
        value: leads[0].value,
        leadName: leads[0].leadName || ""
      }]);
    }
  }, [sendingTo, leads, leadId, setValue]);

  useEffect(() => {
    if (myEmails?.length === 1) {
      setValue("from", [myEmails[0]]);
    }
  }, [myEmails, setValue]);

  const onSubmit = async (data: newEmailSchema) => {

    const payload = {
      ...data,
      to: data.to[0],
      from: data.from[0]?.value,
      replyTo: replyTo
        ? {
            messageId: replyTo.messageId,
            threadId: replyTo.threadId,
            originalSubject: replyTo.subject,
            originalBody: replyTo.body,
            originalSender: replyTo.originalSender,
            originalDate: replyTo.originalDate,
          }
        : undefined,
    };
    sendEmail(payload as any, {
      onSuccess: () => {
        reset();
        toast.success("Message sent successfully");
        onClose();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="p-0! min-w-3xl" showCloseButton={false}>
        <button className="absolute -top-8 left-0" onClick={onClose}>
          <kbd className="border rounded-sm px-2 flex items-center gap-1 py-1 text-xs text-muted-foreground bg-background">
            <HugeiconsIcon icon={CloseIcon} size={14} />
            <span>ESC</span>
          </kbd>
        </button>
        <form className="overflow-hidden" onSubmit={handleSubmit(onSubmit)}>
          <SingleEmailField
            name="to"
            control={control}
            label="To"
            maxSelectedMessage="You can only select one recipient, use Cc for multiple recipients"
            leads={leads}
            onSearch={setQuery}
            actions={
              <CCBCCOptions
                setCc={setCc}
                setBcc={setBcc}
                setValue={setValue}
                cc={cc}
                bcc={bcc}
              />
            }
          />
          {cc && (
            <EmailField
              name="cc"
              maxSelected={100}
              control={control}
              label="Cc"
              leads={leads}
              onSearch={setQuery}
            />
          )}
          {bcc && (
            <EmailField
              name="bcc"
              maxSelected={100}
              control={control}
              label="Bcc"
              leads={leads}
              onSearch={setQuery}
            />
          )}
          {!replyTo && (
            <Controller
              name="subject"
              control={control}
              render={({ field, fieldState }) => (
                <div
                  className={cn(
                    "flex border-b p-1 text-sm",
                    fieldState.error && "bg-destructive/10",
                  )}
                >
                  <div className="text-muted-foreground">Subject:</div>
                  <input
                    className={"w-full outline-0 border-none pl-1"}
                    id="email-subject"
                    {...field}
                  />
                </div>
              )}
            />
          )}
          <SingleEmailField
            name="from"
            control={control}
            label="From"
            maxSelectedMessage="You can only select one sender email"
            leads={myEmails}
            onSearch={() => {}}
          />
          <div className="bg-muted-foreground/5">
            <Controller
              name="body"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex p-1 text-sm">
                  <textarea
                    className={cn(
                      "w-full text-sm! outline-0! ring-0 border-none! pl-1 min-h-56 resize-none",
                      fieldState.error && "bg-destructive/10",
                    )}
                    id="email-body"
                    {...field}
                  />
                </div>
              )}
            />
            {replyTo && (
              <div className="p-2 border-t">
                <div className="text-xs text-muted-foreground mb-1">
                  Replying to {replyTo.originalSender}
                </div>
                <div className="p-2 text-sm border-l-2 border-muted-foreground/30">
                  <div className="font-medium text-xs mb-1">{replyTo.subject}</div>
                  <div className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: replyTo.body }} />
                </div>
              </div>
            )}
            <div className="flex p-1 text-sm">
              <Button disabled={isPending} size="sm" type="submit">
                {isPending ? (
                  <HugeiconsIcon
                    icon={LoadingIcon}
                    size={14}
                    className="animate-spin"
                  />
                ) : (
                  "Send"
                )}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { NewEmailDialog };
